import React from "react";
import { Car, Bike, Plus, Trash2 } from "lucide-react";

export default function VehiclesManager({ vehicles = [], onChange, label = "Registered Vehicles (Cars / Bikes)", helperText = "Add all cars and bikes belonging to this resident flat." }) {
    const list = Array.isArray(vehicles) ? vehicles : [];

    const handleAdd = (type = "car") => {
        onChange([...list, { type, number: "" }]);
    };

    const handleUpdate = (index, field, value) => {
        const next = list.map((item, i) => {
            if (i === index) {
                return { ...item, [field]: value };
            }
            return item;
        });
        onChange(next);
    };

    const handleRemove = (index) => {
        onChange(list.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-2">
            {label && (
                <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold uppercase tracking-widest text-nest-ink2 flex items-center gap-1.5">
                        <Car size={14} className="text-nest-clay" />
                        <span>{label}</span>
                    </label>
                    <span className="text-[11px] text-nest-ink3 font-medium">
                        {list.length} {list.length === 1 ? "vehicle" : "vehicles"}
                    </span>
                </div>
            )}

            {list.length === 0 ? (
                <div className="p-3.5 rounded-xl border border-dashed border-nest-line bg-nest-alt/40 flex items-center justify-between gap-3">
                    <p className="text-xs text-nest-ink3">No cars or bikes added yet.</p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => handleAdd("car")}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-white border border-nest-line hover:border-nest-clay text-nest-ink transition shadow-2xs"
                        >
                            <Car size={13} className="text-nest-clay" /> + Car
                        </button>
                        <button
                            type="button"
                            onClick={() => handleAdd("bike")}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-white border border-nest-line hover:border-nest-clay text-nest-ink transition shadow-2xs"
                        >
                            <Bike size={13} className="text-nest-clay" /> + Bike
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    {list.map((v, idx) => (
                        <div
                            key={idx}
                            className="flex items-center gap-2 p-1.5 sm:p-2 rounded-xl border border-nest-line bg-nest-alt/30 hover:bg-nest-alt/60 transition group"
                        >
                            {/* Type Toggle */}
                            <div className="flex items-center rounded-lg bg-white border border-nest-line p-0.5">
                                <button
                                    type="button"
                                    onClick={() => handleUpdate(idx, "type", "car")}
                                    className={`px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition ${
                                        v.type !== "bike"
                                            ? "bg-nest-clay text-white shadow-2xs"
                                            : "text-nest-ink3 hover:text-nest-ink"
                                    }`}
                                    title="Car"
                                >
                                    <Car size={13} />
                                    <span className="hidden sm:inline">Car</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleUpdate(idx, "type", "bike")}
                                    className={`px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition ${
                                        v.type === "bike"
                                            ? "bg-nest-clay text-white shadow-2xs"
                                            : "text-nest-ink3 hover:text-nest-ink"
                                    }`}
                                    title="Bike / Two-Wheeler"
                                >
                                    <Bike size={13} />
                                    <span className="hidden sm:inline">Bike</span>
                                </button>
                            </div>

                            {/* Plate number input */}
                            <input
                                type="text"
                                value={v.number || ""}
                                onChange={(e) => handleUpdate(idx, "number", e.target.value.toUpperCase())}
                                placeholder={v.type === "bike" ? "e.g. MH 12 CD 5678" : "e.g. MH 12 AB 1234"}
                                className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-nest-line focus:border-nest-clay focus:ring-1 focus:ring-nest-clay outline-none bg-white font-mono text-xs uppercase text-nest-ink placeholder:font-sans placeholder:normal-case placeholder:text-nest-ink3 transition"
                            />

                            {/* Remove button */}
                            <button
                                type="button"
                                onClick={() => handleRemove(idx)}
                                className="p-1.5 text-nest-ink3 hover:text-nest-err rounded-lg hover:bg-red-50 transition"
                                title="Remove vehicle"
                            >
                                <Trash2 size={15} />
                            </button>
                        </div>
                    ))}

                    <div className="flex items-center justify-between pt-1">
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => handleAdd("car")}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-nest-alt hover:bg-nest-sand/40 border border-nest-line text-nest-ink transition"
                            >
                                <Plus size={13} className="text-nest-clay" />
                                <span>Add Car</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleAdd("bike")}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-nest-alt hover:bg-nest-sand/40 border border-nest-line text-nest-ink transition"
                            >
                                <Plus size={13} className="text-nest-clay" />
                                <span>Add Bike</span>
                            </button>
                        </div>
                        {helperText && <span className="text-[11px] text-nest-ink3 hidden sm:inline">{helperText}</span>}
                    </div>
                </div>
            )}
        </div>
    );
}
